import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Chip,
  Typography,
  Alert
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';


const example1 = `
{"bool":{
  "should":[
    {"bool":{"must":[
      {"range":{"user_events_num":{"lte":5}}},
      {"range":{"user_score_spam":{"gte":0.6}}}
    ]}},
    {"bool":{"must":[
      {"range":{"user_events_num":{"gt":5}}},
      {"range":{"user_score_spam":{"gte":0.4}}}
    ]}}
  ]
}}
`


const CustomJsonFilterSection = ({ onUpdate, boostInput = false }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [boost, setBoost] = useState(1);

  // Pre-defined filter templates

  const predefinedFilters = [
    {
      label: 'Example1',
      value: example1.trim()
    }
  ];

  const validateJson = (str) => {
    try {
      if (!str.trim()) {
        return null;
      }
      JSON.parse(str);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleChipClick = (filter) => {
    setJsonInput(filter.value);
    setJsonError('');
  };

  const handleJsonInputChange = (event) => {
    const value = event.target.value;
    setJsonInput(value);
    
    // Validate JSON as user types
    if (value.trim()) {
      const isValid = validateJson(value);
      if (isValid === false) {
        setJsonError('Invalid JSON format');
      } else {
        setJsonError('');
      }
    } else {
      setJsonError('');
    }
  };

  const handleCustomFilterAction = (actionType) => {
    const isValid = validateJson(jsonInput);
    
    if (!jsonInput.trim()) {
      setJsonError('JSON input cannot be empty');
      return;
    }
    
    if (!isValid) {
      setJsonError('Invalid JSON format');
      return;
    }

    try {
      const filterObject = JSON.parse(jsonInput);

      const updateData = {
        action: actionType,
        filter: 'custom',
        field: 'boolean',
        value: filterObject
      };

      // Add boost value if boostInput is enabled
      if (boostInput) {
        updateData.boost = boost;
      }

      onUpdate(updateData);
      
      // Clear the input after successful submission
      setJsonInput('');
      setJsonError('');
    } catch (error) {
      setJsonError('Failed to parse JSON');
    }
  };

  const isJsonValid = jsonInput.trim() ? validateJson(jsonInput) === true : false;

  return (
    <Box>
      {/* Pre-defined Filter Chips */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Quick Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {predefinedFilters.map((filter, index) => (
            <Chip
              key={index}
              label={filter.label}
              onClick={() => handleChipClick(filter)}
              variant="outlined"
              color="primary"
              clickable
            />
          ))}
        </Box>
      </Box>

      {/* JSON Input Text Area */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Custom Filter (JSON)"
          multiline
          rows={12}
          fullWidth
          value={jsonInput}
          onChange={handleJsonInputChange}
          placeholder={"Enter your custom ES filter in JSON format"}
          variant="outlined"
          sx={{ mb: 1 }}
        />
        {jsonError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {jsonError}
          </Alert>
        )}
      </Box>

      {/* Boost Input */}
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
        />
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleCustomFilterAction('include')}
          disabled={!isJsonValid}
        >
          <AddIcon />
        </Button>

        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleCustomFilterAction('boost')}
            disabled={!isJsonValid}
          >
            <TrendingUpIcon />
          </Button>
        )}

        <Button
          variant="contained"
          color="error"
          onClick={() => handleCustomFilterAction('exclude')}
          disabled={!isJsonValid}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default CustomJsonFilterSection;

